/**
 * MCP Notification Bus — pub/sub for MCP command execution events.
 *
 * When any MCP command executes successfully via StudioMcpBus, a notification
 * is emitted through this bus. Views (Page Editor, Flow Editor, Preview, JSON
 * Editor) subscribe to stay synchronized.
 *
 * Replaces the old cross-tab-sync.ts subscriber-based approach.
 */

import type { McpResult } from './types'

export interface McpNotification {
  type: 'mcp:notification'
  tool: string
  params: Record<string, unknown>
  result: McpResult
  timestamp: number
}

export type NotificationListener = (notification: McpNotification) => void

export class McpNotificationBus {
  private listeners = new Set<NotificationListener>()

  /**
   * Subscribe to MCP notifications.
   * @returns Unsubscribe function.
   */
  subscribe(listener: NotificationListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Emit a notification to all subscribers.
   * Errors in individual listeners are caught and logged — they do not
   * prevent other listeners from receiving the notification.
   */
  emit(notification: McpNotification): void {
    for (const listener of this.listeners) {
      try {
        listener(notification)
      } catch (e) {
        console.error('[mcp-notify]', e)
      }
    }
  }
}

/** Singleton notification bus instance. */
export const notificationBus = new McpNotificationBus()
